/* JAI plans — the single place to edit pricing.
 * ──────────────────────────────────────────────────────────────────────────
 * Edit ONLY this file to tweak
 * copy/prices/features — the /pricing page renders whatever is defined here.
 *
 * `monthly` is the per-month price when billed monthly. `annualTotal` is the
 * REAL full amount charged once a year when billed annually — NOT a
 * per-month-equivalent. Always display annualTotal as the yearly total
 * ("$99 billed once yearly"), never divided by 12 without also showing the
 * yearly total alongside it — a bare monthly-equivalent reads as the actual
 * per-month rate and misleads people about what actually gets charged.
 *
 * There is no founding price and no scheduled price rise (removed 27 Oct 2026).
 * People already subscribed keep whatever Stripe Price they signed up on.
 *
 * AI allowance is sold in JAI credits (see CREDITS_EXPLAINER below). The credit
 * numbers shown here MIRROR PLAN_CREDIT_MONTHLY_LIMIT in config/planAccess.js,
 * which mirrors the backend; backend/tests/unit/creditsParity.test.js fails if
 * the three drift.
 *
 * IMPORTANT (backend): monthly/annualTotal must match the four Stripe prices
 * created in the Dashboard exactly — STRIPE_PRICE_PRO_MONTHLY,
 * STRIPE_PRICE_PRO_ANNUAL, STRIPE_PRICE_ELITE_MONTHLY,
 * STRIPE_PRICE_ELITE_ANNUAL (see engineering/billing/JAI_STRIPE.md). If you change a price
 * here, update the matching Stripe price too (or create a new one and swap
 * the env var) — this file does not control what Stripe actually charges.
 * ────────────────────────────────────────────────────────────────────────── */

import { IS_NATIVE_APP } from '../services/runtime'
import { PLAN_CREDIT_MONTHLY_LIMIT } from './planAccess'

/** One line under the plan list explaining what a credit is. */
export const CREDITS_EXPLAINER =
  'A quick question uses 1 credit; bigger jobs like a full draft strategy use more.'

export const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Free forever — get started with JAI',
    monthly: 0,
    annualTotal: 0,
    cta: 'Continue with Starter',
    free: true,
    features: [
      '1 connected league',
      `${PLAN_CREDIT_MONTHLY_LIMIT.starter} JAI credits a month`,
      'Waiver Wire & Trade Analyser',
      // Not rendered on native at all (this array only ever reaches the UI
      // via PlanPicker.jsx, which only mounts under /pricing — unregistered
      // on native, Guideline 3.1.1). Kept native-safe anyway rather than
      // relying on that being true forever.
      IS_NATIVE_APP
        ? 'Draft Room — full analysis for rounds 1-5'
        : 'Draft Room — full analysis for rounds 1-5, then upgrade for the rest',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Every launch tool, every week',
    monthly: 11.99,
    annualTotal: 99,
    cta: 'Choose Pro',
    badge: 'Most popular',
    trialDays: 7,
    features: [
      'Everything in Starter, plus:',
      'Up to 3 connected leagues',
      `${PLAN_CREDIT_MONTHLY_LIMIT.pro} JAI credits a month`,
      'All 9 launch tools: Waiver Wire, Trade Analyser, Player Analyser, Team Analyser, Draft Room (full, every round), Opponent Scout, Schedule Surgeon, Injury Insurance & My Notes',
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    tagline: 'More leagues, first access to everything new',
    monthly: 18.99,
    annualTotal: 199,
    cta: 'Choose Elite',
    trialDays: 7,
    features: [
      'Everything in Pro, plus:',
      'Up to 6 connected leagues',
      `${PLAN_CREDIT_MONTHLY_LIMIT.elite} JAI credits a month`,
      'All 9 main tools + ongoing tool drops (new tools added throughout the season)',
    ],
  },
]

/** The number to display for this plan at this billing cycle — for 'annual'
 *  this is the real full yearly charge, not a per-month rate. */
export function planPrice(plan, billing) {
  return billing === 'annual' ? plan.annualTotal : plan.monthly
}

/** Annual total expressed as a monthly-equivalent, for display ONLY
 *  alongside the real yearly total (never on its own — see file header). */
export function annualMonthlyEquivalent(plan) {
  return plan.annualTotal / 12
}

/** How much cheaper annual billing is than paying the monthly rate
 *  for 12 months, computed from this plan's actual numbers — not a
 *  hardcoded guess. (Pro: 31% · Elite: 13%, as of the prices above.) Returns
 *  null for free plans. */
export function annualSavingsLabel(plan) {
  if (!plan.monthly) return null
  const pct = Math.round((1 - plan.annualTotal / (plan.monthly * 12)) * 100)
  return `Save ~${pct}%`
}

// Display + billing currency: USD. JAI's primary market is US-based NFL/NBA
// players, so prices are shown (and charged) in US dollars — NOT AUD, even
// though the business is AU-based. The four Stripe prices in the Dashboard
// must be created in USD to match (see engineering/billing/JAI_STRIPE.md); Stripe then
// settles to the account's AUD balance automatically (small FX fee applies).
export function fmtPrice(n) {
  if (n === 0) return '$0'
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`
}
